import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, Upload, ImageIcon, AlertCircle, CheckCircle, Star } from 'lucide-react';
import { Editor } from '@tinymce/tinymce-react';
import { API_BASE_URL } from '../config';

const AddProductModal = ({ isOpen, onClose, onProductAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    care_instructions: '',
    shipping_info: '',
    status: 'active',
    tags: '',
    seo_title: '',
    meta_description: '',
    base_price: '',
    sale_price: '',
    sku: '',
    quantity: 0
  });
  
  const [images, setImages] = useState([]);
  const [measurementImage, setMeasurementImage] = useState(null);
  const [measurementImageLoading, setMeasurementImageLoading] = useState(false);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const measurementImageInputRef = useRef(null);
  const measurementImageFileRef = useRef(null);

  // Variants state
  const [variants, setVariants] = useState([]);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantValue, setNewVariantValue] = useState('');
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditorChange = (field) => (content) => {
    setFormData(prev => ({
      ...prev,
      [field]: content
    }));
  };

  const measurementPreviewUrl = useMemo(() => {
    if (!measurementImage) return null;
    return URL.createObjectURL(measurementImage);
  }, [measurementImage]);

  useEffect(() => {
    return () => {
      if (measurementPreviewUrl) {
        URL.revokeObjectURL(measurementPreviewUrl);
      }
    };
  }, [measurementPreviewUrl]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (files.length > 6) {
      setError('Maximum 6 images allowed');
      return;
    }

    // Validate file size and type
    const validFiles = [];
    for (let file of files) {
      
      if (file.size > 1024 * 1024) { // 1MB
        setError(`Image "${file.name}" is too large. Maximum size is 1MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      
      validFiles.push(file);
    }

    setImages(validFiles);
    setPrimaryImageIndex(null); // No primary image selected by default
    setError('');
  };

  const handleMeasurementImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB
        setError(`Measurement image is too large. Maximum size is 1MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      setMeasurementImage(file);
      measurementImageFileRef.current = file;
      setMeasurementImageLoading(true);
      setError('');
      if (measurementImageInputRef.current) {
        measurementImageInputRef.current.value = '';
      }
    }
  };

  const removeMeasurementImage = () => {
    setMeasurementImage(null);
    setMeasurementImageLoading(false);
    measurementImageFileRef.current = null;
    if (measurementImageInputRef.current) {
      measurementImageInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    
    // Adjust primary image index if needed
    if (primaryImageIndex !== null) {
      if (images.length > 1) {
        if (index === primaryImageIndex) {
          // If removing the primary image, clear primary selection
          setPrimaryImageIndex(null);
        } else if (index < primaryImageIndex) {
          // If removing an image before primary, adjust index
          setPrimaryImageIndex(prev => prev - 1);
        }
      } else {
        // If only one image left, clear primary selection
        setPrimaryImageIndex(null);
      }
    }
  };

  const setPrimaryImage = (index) => {
    setPrimaryImageIndex(index);
  };

  // Variant management functions
  const addVariantName = () => {
    if (!newVariantName.trim()) {
      setError('Variant name is required');
      return;
    }

    // Check if variant name already exists
    if (variants.some(v => v.name.toLowerCase() === newVariantName.trim().toLowerCase())) {
      setError('Variant name already exists');
      return;
    }

    const newVariant = {
      name: newVariantName.trim(),
      values: []
    };

    setVariants(prev => [...prev, newVariant]);
    setNewVariantName('');
    setError('');
  };

  const addVariantValue = () => {
    if (selectedVariantIndex === null || selectedVariantIndex === '' || !newVariantValue.trim()) {
      setError('Please select a variant and enter a value');
      return;
    }

    const variant = variants[selectedVariantIndex];
    if (!variant) {
      setError('Selected variant not found');
      return;
    }

    if (variant.values.includes(newVariantValue.trim())) {
      setError('This value already exists for this variant');
      return;
    }

    setVariants(prev => prev.map((variant, index) => {
      if (index === selectedVariantIndex) {
        return {
          ...variant,
          values: [...variant.values, newVariantValue.trim()]
        };
      }
      return variant;
    }));

    setNewVariantValue('');
    setError('');
  };

  const removeVariant = (variantIndex) => {
    setVariants(prev => prev.filter((_, index) => index !== variantIndex));
    if (selectedVariantIndex === variantIndex) {
      setSelectedVariantIndex(null);
    }
  };

  const removeVariantValue = (variantIndex, valueIndex) => {
    setVariants(prev => prev.map((variant, index) => {
      if (index === variantIndex) {
        return {
          ...variant,
          values: variant.values.filter((_, vIndex) => vIndex !== valueIndex)
        };
      }
      return variant;
    }));
  };

  const validateForm = () => {
  if (!formData.name.trim()) {
    setError('Product name is required');
    return false;
  }
  
  if (!formData.base_price || formData.base_price <= 0) {
    setError('Valid base price is required');
    return false;
  }
  
  if (formData.sale_price && formData.sale_price <= 0) {
    setError('Sale price must be greater than 0');
    return false;
  }
  
const basePrice = parseFloat(formData.base_price);
const salePrice = parseFloat(formData.sale_price);

if (!isNaN(salePrice) && salePrice >= basePrice) {
  setError('Sale price must be less than base price');
  return false;
}

  
  return true;
};
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      care_instructions: '',
      shipping_info: '',
      status: 'active',
      tags: '',
      seo_title: '',
      meta_description: '',
      base_price: '',
      sale_price: '',
      sku: '',
      quantity: 0
    });
    setImages([]);
    setMeasurementImage(null);
    setMeasurementImageLoading(false);
    measurementImageFileRef.current = null;
    if (measurementImageInputRef.current) {
      measurementImageInputRef.current.value = '';
    }
    setPrimaryImageIndex(null);
    setVariants([]);
    setNewVariantName('');
    setNewVariantValue('');
    setSelectedVariantIndex(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token missing. Please login again.');
        setLoading(false);
        return;
      }

      // Prepare FormData
      const submitData = new FormData();
      
      // Add form fields
      submitData.append('name', formData.name);
      submitData.append('description', formData.description || '');
      submitData.append('care_instructions', formData.care_instructions || '');
      submitData.append('shipping_info', formData.shipping_info || '');
      submitData.append('base_price', formData.base_price);
      submitData.append('sale_price', formData.sale_price || '');
      submitData.append('quantity', formData.quantity || '0');
      submitData.append('status', formData.status);
      submitData.append('tags', formData.tags || '');
      submitData.append('seo_title', formData.seo_title || '');
      submitData.append('meta_description', formData.meta_description || '');
      submitData.append('sku', formData.sku || '');

      // Add images
      if (images.length > 0) {
        images.forEach((image, index) => {
          submitData.append('images', image);
        });
        
        // Add primary image index (null if none selected)
        if (primaryImageIndex !== null) {
          submitData.append('primary_image_index', primaryImageIndex.toString());
        } else {
          submitData.append('primary_image_index', ''); // Empty string for no primary image
        }
      } else {
        // If no images, set primary image index to empty
        submitData.append('primary_image_index', '');
      }

      // Add measurement image
      const measurementFile = measurementImage || measurementImageFileRef.current;
      if (measurementFile) {
        const fileName = measurementFile.name || 'measurement-image.png';
        submitData.append('measurement_image', measurementFile, fileName);
      }

      // Add variants
      if (variants.length > 0) {
        submitData.append('variants', JSON.stringify(variants));
      } else {
        submitData.append('variants', JSON.stringify([]));
      }

      const response = await fetch(`${API_BASE_URL}/addproduct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData,
      });

      // Try to parse response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const textResponse = await response.text();
        setError('Server returned invalid response');
        setLoading(false);
        return;
      }

      if (response.ok) {
        if (data.success) {
          let primaryImageText = '';
          if (images.length > 0 && primaryImageIndex !== null) {
            primaryImageText = ` with Image ${primaryImageIndex + 1} as primary`;
          } else if (images.length > 0) {
            primaryImageText = ' (no primary image selected)';
          }
          setSuccess(`Product added successfully!${primaryImageText}`);
          
          // Notify parent
          if (onProductAdded) {
            onProductAdded();
          }
          
          // Reset form and close modal after success
          setTimeout(() => {
            resetForm();
            onClose();
          }, 1500);
          
        } else {
          setError(data.message || data.error || 'Product add failed');
        }
      } else {
        setError(data.error || data.message || `Server error: ${response.status}`);
      }

    } catch (networkError) {
      setError(`Network error: ${networkError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Add New Product</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter product name"
                disabled={loading}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.description}
                onEditorChange={handleEditorChange('description')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 300,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Care Instructions
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.care_instructions}
                onEditorChange={handleEditorChange('care_instructions')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 250,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipping & Order Processing
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.shipping_info}
                onEditorChange={handleEditorChange('shipping_info')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 250,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Original Price (MRP) *
              </label>
              <input
                type="number"
                name="base_price"
                value={formData.base_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading}
                required
              />
              <p className="text-xs text-gray-500 mt-1">This will show as strikethrough price if sale price is set</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sale Price (Current Price)
              </label>
              <input
                type="number"
                name="sale_price"
                value={formData.sale_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">Actual selling price (must be less than original price)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SKU
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Product SKU"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="tag1, tag2, tag3"
                disabled={loading}
              />
            </div>
          </div>

          {/* SEO Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title
                </label>
                <input
                  type="text"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SEO optimized title"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Meta description for search engines"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Variants Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Variants</h3>
            
            {/* Add Variant Name */}
            <div className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  placeholder="Enter variant name (e.g., Color, Size)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={addVariantName}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  Add Variant
                </button>
              </div>

              {/* Add Variant Values */}
              {variants.length > 0 && (
                <div className="flex space-x-2">
                  <select
                    value={selectedVariantIndex !== null ? selectedVariantIndex : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedVariantIndex(value === '' ? null : parseInt(value));
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="">Select variant to add values</option>
                    {variants.map((variant, index) => (
                      <option key={index} value={index}>{variant.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newVariantValue}
                    onChange={(e) => setNewVariantValue(e.target.value)}
                    placeholder="Enter value (e.g., Red, Large)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={addVariantValue}
                    disabled={loading || selectedVariantIndex === null}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Add Value
                  </button>
                </div>
              )}

              {/* Display Variants */}
              {variants.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Current Variants:</h4>
                  {variants.map((variant, variantIndex) => (
                    <div key={variantIndex} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{variant.name}</h5>
                        <button
                          type="button"
                          onClick={() => removeVariant(variantIndex)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          disabled={loading}
                        >
                          Remove Variant
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {variant.values.map((value, valueIndex) => (
                          <span
                            key={valueIndex}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                          >
                            {value}
                            <button
                              type="button"
                              onClick={() => removeVariantValue(variantIndex, valueIndex)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              disabled={loading}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {variant.values.length === 0 && (
                          <span className="text-sm text-gray-500 italic">No values added yet</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Upload */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>
            
                          <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Images (Max 6, 1MB each)
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Select a primary image by clicking on any image, or leave unselected to use default ordering.
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload-add"
                    disabled={loading}
                  />
                  <label
                    htmlFor="image-upload-add"
                    className="cursor-pointer flex flex-col items-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click to upload product images
                    </span>
                      <span className="text-xs text-gray-500">
                        PNG, JPG, JPEG, GIF, WEBP up to 1MB each
                      </span>
                  </label>
                </div>
              </div>

              {/* Image Preview */}
              {images.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Images ({images.length}/6) - Select Primary Image
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Preview ${index + 1}`}
                          className={`w-full h-24 object-cover rounded-lg border-2 ${
                            index === primaryImageIndex 
                              ? 'border-blue-500 border-3' 
                              : 'border-gray-200'
                          }`}
                        />
                        
                        {/* Primary Image Indicator */}
                        {index === primaryImageIndex && (
                          <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                            <Star className="h-3 w-3 fill-current" />
                          </div>
                        )}
                        
                        {/* Set Primary Button */}
                        {index !== primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute top-1 left-1 bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={loading}
                            title="Set as primary image"
                          >
                            <Star className="h-3 w-3" />
                          </button>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={loading}
                        >
                          ×
                        </button>
                        
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                          {index + 1}
                        </div>
                        
                        {/* Primary Image Label */}
                        {index === primaryImageIndex && (
                          <div className="absolute bottom-1 right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Primary Image Info */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-blue-700">
                        <Star className="h-4 w-4 mr-2 fill-current" />
                        <span>
                          {primaryImageIndex !== null ? (
                            <>
                              <strong>Primary Image:</strong> Image {primaryImageIndex + 1} will be set as the main product image
                              <span className="block text-xs text-blue-600 mt-1">
                                This image will appear first in product listings and as the main thumbnail
                              </span>
                            </>
                          ) : (
                            <>
                              <strong>Primary Image:</strong> No primary image selected
                              <span className="block text-xs text-blue-600 mt-1">
                                Click on any image to set it as primary, or leave unselected to use default ordering
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                      {primaryImageIndex !== null && (
                        <button
                          type="button"
                          onClick={() => setPrimaryImage(null)}
                          className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                          disabled={loading}
                        >
                          Clear Primary
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No images selected</p>
                  <p className="text-sm">Upload images to optionally set one as primary</p>
                </div>
              )}
            </div>
          </div>

          {/* Measurement Image Upload */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Measurement Image (Optional)</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Measurement Chart
                </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Upload a size guide or measurement chart for customers (Max 1MB)
                  </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMeasurementImageChange}
                    className="hidden"
                    id="measurement-image-upload"
                    ref={measurementImageInputRef}
                    disabled={loading}
                  />
                  <label
                    htmlFor="measurement-image-upload"
                    className="cursor-pointer flex flex-col items-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click to upload measurement image
                    </span>
                          <span className="text-xs text-gray-500">
                            PNG, JPG, JPEG, GIF, WEBP up to 1MB
                          </span>
                  </label>
                </div>
              </div>

              {/* Measurement Image Preview */}
              {measurementImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Measurement Image Preview
                  </label>
                  <div className="relative inline-block">
                    {measurementImageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg z-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                    {measurementPreviewUrl && (
                      <img
                        src={measurementPreviewUrl}
                        alt="Measurement preview"
                        className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200"
                        onLoad={() => setMeasurementImageLoading(false)}
                        onError={() => {
                          setMeasurementImageLoading(false);
                          setMeasurementImage(null);
                          measurementImageFileRef.current = null;
                          setError('Unable to preview measurement image. Please try a different file.');
                        }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={removeMeasurementImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t pt-6 flex space-x-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Add Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;